// src/components/wellfinder/RecordFacts.jsx
//
// Record facts and the event history.
//
// Almost everything here is null on today's corpus — no record has been
// adjudicated, detection_confidence is null for all 10,803, and every history
// is a single `assigned` event. That is the correct state and it must read as
// such. The specific trap is detection confidence: it is NULL, not zero, and
// rendering `0` or `0%` would assert the model had no confidence in a detection
// it did in fact make. The synthetic-confidence check in
// 026_wellfinder_adjudication_verify.sql exists because a previous revision
// stored one.

import { seqNum, bySeq } from "../../stores/useWfQueueStore";
import { WF_FLAG_REASON_LABEL } from "../../constants/wfFlagReasons";
import { WF_REJECT_REASON_LABEL } from "../../constants/wfRejectReasons";

const TIER_LABEL = { a: "A", a_prime: "A′", b: "B", c: "C", d: "D" };

// Null and "unavailable" are different from zero and from empty. One helper so
// the distinction cannot be made inconsistently across a dozen fields.
function Unavailable({ why = "not available" }) {
  return <span className="text-fg-secondary/50 italic">{why}</span>;
}

function Row({ label, children }) {
  return (
    <div className="flex gap-2 py-0.5">
      <span className="w-32 shrink-0 text-fg-secondary/70">{label}</span>
      {/* min-w-0 lets the BOX shrink; break-all is what lets the CONTENT give.
          detection_source_id is "Well_Head_" plus 32 hex characters — one token
          with no break opportunity in it — so without break-all the row
          overflows its container no matter how small the box is allowed to be.
          Not truncate: an analyst copying a source id needs all of it. */}
      <span className="min-w-0 flex-1 break-all text-fg-secondary">{children}</span>
    </div>
  );
}

function age(ts) {
  if (!ts) return null;
  const ms = Date.now() - new Date(ts).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const min = Math.floor(ms / 60000);
  return min < 1 ? "just now" : `${min} min`;
}

const fmtTime = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 19).replace("T", " ");
};

/** Provenance for one adjudicated value: who or what put it there. */
function Provenance({ entry }) {
  if (!entry) return null;
  return (
    <span className="ml-2 text-[10px] text-fg-secondary/60">
      {entry.source === "rrc" ? "adopted from registry" : "set by analyst"}
    </span>
  );
}

// Per-event-type payload facts. A generic JSON dump would be honest and
// useless; these are the fields that answer "what changed" for each type.
function EventDetail({ e }) {
  const p = e.payload ?? {};
  switch (e.event_type) {
    case "assigned":
      return (
        <>
          tier {TIER_LABEL[p.tier] ?? p.tier ?? "?"}
          {p.d1 != null && <> · nearest {Number(p.d1).toFixed(1)} m</>}
          {p.d2 != null && <> · second {Number(p.d2).toFixed(1)} m</>}
        </>
      );
    case "match_set":
      return (
        <>
          candidate #{p.candidate_rank}
          {p.api && <> · {p.api}</>}
          {p.distance_m != null && <> · {Number(p.distance_m).toFixed(1)} m</>}
        </>
      );
    // Distinct from match_set since migration 033: the confidence assertion was
    // withdrawn, the linkage did not move.
    case "match_downgraded":
      return (
        <>
          confidence {p.before?.match_confidence ?? "?"} →{" "}
          {p.after?.match_confidence ?? "?"}
        </>
      );
    case "match_cleared":
      return <>match removed</>;
    case "attribute_set":
      return (
        <>
          {p.attribute ?? p.key}
          {p.after !== undefined && <> → {String(p.after ?? "null")}</>}
        </>
      );
    case "geometry_adjusted":
      return <>geometry moved</>;
    default:
      return null;
  }
}

export default function RecordFacts({ detail }) {
  const r = detail?.record;
  if (!r) return null;
  const prov = detail.provenance ?? {};
  const claimAge = age(r.claimed_at);

  // Coerced. seq is bigint and node-pg serialises it as a STRING, so a raw
  // subtraction is NaN and a raw comparison is lexicographic ("9" > "10").
  const events = [...(detail.events ?? [])].sort(bySeq);

  return (
    <div className="space-y-4 text-xs">
      <section>
        <h3 className="mb-1.5 text-[11px] font-mono uppercase tracking-wide text-fg-secondary">
          Record
        </h3>
        <Row label="Tier">{TIER_LABEL[r.tier] ?? r.tier}</Row>
        <Row label="Status">{r.status}</Row>
        {/* Only on an escalated record. This is the supervisor's whole reason
            for opening it, so it sits directly under the status rather than in
            a panel of its own — the decision is made here, next to the map and
            the commit bar, which is why the flagged queue is a filter on this
            page and not a separate screen.

            The flag NOTE is deliberately not repeated here: it is in the
            timeline below, attached to the flag event, where it reads as
            something a person wrote at a moment rather than a field. */}
        {/* ── QC STATE, ONLY WHEN THERE IS SOME ────────────────────────────
            Gated on reviewed_at rather than on status. A record can sit in a
            non-pending status with no reviewer -- batch_job writes one -- and
            the question these rows answer is "who decided this, and when",
            which has no answer until someone did.

            10,800 of 10,800 records are pending today, so an ungated block
            would render an empty "Reviewed by —" on every single one. A panel
            that shows a blank field 10,800 times has taught the analyst to
            stop reading it by the time it matters.

            This is where the verdict lives now. It used to be projected onto
            the detection row in vector_assets_org as qc_status/qc_reviewer/
            qc_reviewed_at/qc_notes; that projection existed because detections
            were replicated across four orgs and is gone with the replication.
            The record has carried these columns all along. */}
        {r.reviewed_at && (
          <>
            <Row label="Reviewed by">
              {r.reviewed_by_name ?? (
                // batch_job decisions have no person behind them. Saying so is
                // better than an empty cell, which reads as missing data.
                <span className="text-fg-secondary">system</span>
              )}
            </Row>
            <Row label="Reviewed">
              <span className="tabular-nums">
                {new Date(r.reviewed_at).toLocaleString()}
              </span>
            </Row>
            {/* Individual is the overwhelming default and adds nothing; bulk
                and batch_job change how much weight the decision carries. */}
            {r.review_mode && r.review_mode !== "individual" && (
              <Row label="Decided in">{r.review_mode.replace("_", " ")}</Row>
            )}
            {r.status === "rejected" && r.reject_reason && (
              <Row label="Reject reason">
                {WF_REJECT_REASON_LABEL[r.reject_reason] ?? r.reject_reason}
              </Row>
            )}
          </>
        )}
        {r.status === "needs_review" && r.flag_reason && (
          <Row label="Escalated">
            <span className="text-amber-300">
              {WF_FLAG_REASON_LABEL[r.flag_reason] ?? r.flag_reason}
            </span>
          </Row>
        )}
        <Row label="Detection">
          <span className="tabular-nums">{r.detection_source_id ?? r.source_id}</span>
        </Row>
        <Row label="Nearest at assign">
          {r.initial_distance_m != null ? (
            <span className="tabular-nums">{r.initial_distance_m.toFixed(1)} m</span>
          ) : (
            // Tier C has no candidates, so the distance is not missing — it is
            // undefined. Say which.
            <Unavailable why={r.tier === "c" ? "no candidate to measure to" : "not recorded"} />
          )}
        </Row>
        <Row label="Model confidence">
          {r.detection_confidence != null ? (
            <span className="tabular-nums">{r.detection_confidence}</span>
          ) : (
            // NOT 0. The vision run did not emit a per-detection confidence for
            // this corpus; a zero here would be a claim the model never made.
            <Unavailable why="not emitted by this detection run" />
          )}
        </Row>
        <Row label="Claim">
          {r.claim_active ? (
            <span className="text-amber-300/90">
              {r.claimed_by_name || "another analyst"}
              {claimAge ? ` · ${claimAge}` : ""}
            </span>
          ) : (
            <span className="text-fg-secondary/60">unclaimed</span>
          )}
        </Row>
      </section>

      <section>
        <h3 className="mb-1.5 text-[11px] font-mono uppercase tracking-wide text-fg-secondary">
          Adjudicated values
        </h3>
        {/* All four are null until 3c. Rendered as rows anyway so the analyst
            can see what adjudication will fill in, rather than an empty box. */}
        <Row label="API10">
          {r.api10 ? (
            <>
              <span className="tabular-nums">{r.api10}</span>
              <Provenance entry={prov.api10} />
            </>
          ) : <Unavailable why="not set" />}
        </Row>
        <Row label="Well status">
          {r.well_status ? (
            <>{r.well_status}<Provenance entry={prov.well_status} /></>
          ) : <Unavailable why="not set" />}
        </Row>
        <Row label="Oil / gas">
          {r.oil_gas_code ? (
            <>{r.oil_gas_code}<Provenance entry={prov.oil_gas_code} /></>
          ) : <Unavailable why="not set" />}
        </Row>
        <Row label="Equipment">
          {r.equipment_type ? (
            <>{r.equipment_type}<Provenance entry={prov.equipment_type} /></>
          ) : <Unavailable why="not set" />}
        </Row>
      </section>

      <section>
        <h3 className="mb-1.5 text-[11px] font-mono uppercase tracking-wide text-fg-secondary">
          History
        </h3>
        {/* One `assigned` event per record today, and that is right — the log
            fills in from 3c onward. A thin history is not an empty one. */}
        {/* SEQ IS NOT ON THE ROW. It was a w-4 column — 16px, enough for one
            digit — and the corpus is already at seq 10,945, so four digits
            overflowed the track and painted over the event type. Widening the
            column was the wrong fix: seq is internal ordering and means nothing
            to an analyst. It is real, and it is not information to the reader.
            It stays as the sort key and as a title attribute for anyone tracing
            an audit question, and it costs the row nothing. */}
        <ol className="space-y-1" data-testid="event-history">
          {events.map((e) => (
            <li
              key={e.id ?? seqNum(e)}
              title={`seq ${seqNum(e)}`}
              data-seq={seqNum(e)}
              className="min-w-0"
            >
              <div className="min-w-0">
                <span className="text-fg-primary">{e.event_type}</span>{" "}
                <span className="text-fg-secondary/70">
                  <EventDetail e={e} />
                </span>
                <div className="min-w-0 truncate text-[10px] text-fg-secondary/60">
                  {/* batch_job carries a null actor by constraint — the
                      assignment worker is not a person and must not be
                      rendered as an anonymous one. */}
                  {e.review_mode === "batch_job" ? "system" : e.actor || "unknown"}
                  {/* In the payload specifically so a platform admin operating
                      AS DreamHammer is distinguishable from a native analyst.
                      An audit log that cannot tell those apart is not one. */}
                  {e.payload?.is_impersonating && (
                    <span className="ml-1 rounded bg-accent-action/20 px-1 text-accent-action">
                      impersonating
                    </span>
                  )}
                  {" · "}
                  {fmtTime(e.created_at)}
                  {e.review_mode && e.review_mode !== "individual" && (
                    <> · {e.review_mode}</>
                  )}
                </div>
                {e.note && (
                  <div className="mt-0.5 border-l border-bg-border pl-2 text-[11px] text-fg-secondary">
                    {e.note}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}
